import ReactSelect, {ActionMeta, MultiValue, SingleValue} from 'react-select'
import CreatableSelect from 'react-select/creatable'
import {customSelectStyles, StyledContainer} from './style.ts'
import {useTheme} from 'styled-components'
import Label from '@components/ui/Label'
import {ReactNode, RefCallback, useCallback} from 'react'
import InputHelpText from '@components/ui/InputHelpText'
import {customComponents} from '@components/commons/Select/commons.tsx'
import {DefaultTFuncReturn} from 'i18next'

export type SelectValue = {
    label: string | ReactNode | unknown
    value: string
}

export interface SelectProps {
    className?: string
    name?: string
    options: SelectValue[]
    label?: string | DefaultTFuncReturn
    placeholder?: string | DefaultTFuncReturn
    helpText?: string | DefaultTFuncReturn
    readOnly?: boolean
    defaultValue?: SelectValue | []
    isSearchable?: boolean
    isClearable?: boolean
    isCreatable?: boolean
    formatCreateLabel?: (value: string) => ReactNode
    addOptionMessage?: string
    maxItems?: number
    isMulti?: boolean
    closeMenuOnSelect?: boolean
    disabled?: boolean
    size?: 'medium' | 'large'
    /**
     * How to use the onChange based on controlled or uncontrolled select:
     *
     * uncontrolled: onChange={(event) => console.log(event)}
     * controlled multi: onChange={(newValue) => {onChange(newValue as SelectValue[])}}
     * controlled single: onChange={(newValue) => {onChange([newValue] as SelectValue[])}}
     */
    onChange?: (
        newValue: SingleValue<SelectValue> | MultiValue<SelectValue> | SelectValue[],
        actionMeta: ActionMeta<SelectValue>
    ) => void
    value?: SelectValue | SelectValue[]
    ref?: RefCallback<unknown>
    errorMessage?: string | DefaultTFuncReturn
}

const InputSelect = ({
    className,
    name,
    options = [],
    label,
    placeholder,
    helpText,
    defaultValue,
    isSearchable = false,
    isCreatable = false,
    addOptionMessage = '',
    maxItems = 100,
    isMulti = false,
    closeMenuOnSelect = !isMulti,
    disabled,
    onChange,
    errorMessage,
    ...rest
}: SelectProps) => {
    const theme = useTheme()

    // Label for new item creation
    const createLabel = useCallback(
        (value: string) => (
            <span style={{fontSize: 14}}>
                {addOptionMessage}
                <span>{value}</span>
            </span>
        ),
        [addOptionMessage]
    )

    const selectProps = {
        options,
        name,
        closeMenuOnSelect,
        isSearchable,
        isCreatable,
        isMulti,
        isDisabled: rest.readOnly || disabled,
        classNamePrefix: isCreatable ? 'creatable_select' : 'select',
        placeholder,
        createLabel,
        disabled,
        maxItems,
        defaultValue,
        onChange,
        ...rest
    }

    const selectComponentProps = {
        formatCreateLabel: createLabel,
        components: customComponents,
        ...selectProps,
        styles: customSelectStyles({theme, isError: !!errorMessage})
    }

    return (
        <StyledContainer className={className}>
            {label && <Label htmlFor={name}>{label}</Label>}
            {isCreatable ? <CreatableSelect {...selectComponentProps} /> : <ReactSelect {...selectComponentProps} />}
            <InputHelpText helpText={helpText} error={errorMessage} />
        </StyledContainer>
    )
}

export default InputSelect

InputSelect.displayName = 'InputSelect'
